/**
 * Единый источник детекции ролей на фронте (зеркало backend PlatformRolePolicy /
 * OrgRolePolicy). Чистые функции без React — используются и хуком
 * `usePermissions()`, и не-компонентным кодом. Нормализуют разнобой строковых
 * сравнений (`"SiteAdmin"` / `"owner"` / `.includes("admin")`). См. KAN-25.
 */

/** Минимум полей профиля, нужных для платформенных проверок. */
export type PlatformRoleProfile =
  | {
      platformRoles?: string[] | null;
      primaryPlatformRole?: string | null;
      isAdmin?: boolean | null;
      isSiteAdmin?: boolean | null;
    }
  | null
  | undefined;

const eq = (a: string | null | undefined, b: string): boolean =>
  typeof a === "string" && a.toLowerCase() === b.toLowerCase();

/** Есть ли у профиля платформенная роль (по primaryPlatformRole или в списке). */
export function hasPlatformRole(
  profile: PlatformRoleProfile,
  role: string,
): boolean {
  if (!profile) return false;
  if (eq(profile.primaryPlatformRole, role)) return true;
  const roles = profile.platformRoles;
  return Array.isArray(roles) && roles.some((r) => eq(r, role));
}

/** SiteAdmin: серверный флаг ИЛИ роль в primary/списке. */
export function isSiteAdmin(profile: PlatformRoleProfile): boolean {
  return Boolean(profile?.isSiteAdmin) || hasPlatformRole(profile, "SiteAdmin");
}

export function isDeveloper(profile: PlatformRoleProfile): boolean {
  return hasPlatformRole(profile, "Developer");
}

/** Модератор платформы (историч. значения роли: "Moder" / "Moderator"). */
export function isModerator(profile: PlatformRoleProfile): boolean {
  return (
    hasPlatformRole(profile, "Moder") || hasPlatformRole(profile, "Moderator")
  );
}

/**
 * Модератор и никто больше: в админке ему видна только «Модерация», а корень
 * /admin для него бесполезен. Роль ищется и в primaryPlatformRole, и в списке —
 * прежняя инлайновая проверка смотрела только на primary и пропускала
 * модератора, у которого роль лежит в platformRoles.
 */
export function isModeratorOnly(profile: PlatformRoleProfile): boolean {
  return !isSiteAdmin(profile) && !isDeveloper(profile) && isModerator(profile);
}

// ── Роли внутри организации ──────────────────────────────────────────────
// Значение приходит из контекстного DTO (organization.currentUserRole),
// а не из профиля. Регистр нормализуется.

export function isOrgAdminRole(role: string | null | undefined): boolean {
  return eq(role, "Owner") || eq(role, "Admin");
}

export function isOrgStaffRole(role: string | null | undefined): boolean {
  return eq(role, "Owner") || eq(role, "Admin") || eq(role, "Moderator");
}

export function isOrgOwnerRole(role: string | null | undefined): boolean {
  return eq(role, "Owner");
}
